import cardLogoStyles from '../../common/styles/card-logo-styles';
import emit from '../../common/events';
import { BaseComponent } from '../base-component/base-component';
import { ModalA11y, ModalBody, ModalButton, ModalConfig, ModalContext, ModalSize } from './interfaces/modal.interface';
import { customElement, state } from 'lit/decorators.js';
import { html, css, TemplateResult } from 'lit';
import { nothing } from 'lit-html';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { waitForElementAndFocus } from '../../common/utils/a11y-util';
import { waitForElementsToLoad } from '../../common/utils/delay-util';

@customElement('mhecomm-modal')
export class Modal extends BaseComponent {
  static styles = [
    css`
      /* .uxt-mhcorp {
      position: fixed;
      z-index: 1060;
      left: 0;
      width: 100%;
      height: 100%;
      overflow: auto;
      background-color: rgb(0,0,0);
      background-color: rgba(0,0,0,0.8);
      transition: opacity .15s linear;
    } */
      .cardLogo {
        width: 4rem;
        height: 2.5rem;
      }
      .cardImg {
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
      }
      .bg-blur {
        backdrop-filter: blur(3px);
      }
      .modal-dialog-centered {
        max-width: fit-content !important;
      }
      .btn-stacked {
        display: grid !important;
        grid-template-areas: 'stack';
      }
      .btn-loader {
        grid-area: stack;
        place-self: center;
        visibility: hidden;
      }
      .btn-loader.active {
        visibility: visible;
      }
      .btn-label {
        grid-area: stack;
        place-self: center;
      }
      .btn-label.hidden {
        visibility: hidden;
      }
    `,
    cardLogoStyles,
  ];

  /**
   * The header of the modal
   */
  @state()
  protected header?: string;

  @state()
  protected headerClassName?: string;

  /**
   * The header text case of the modal
   */
  @state()
  private isHeaderTextBold: boolean | undefined;

  /**
   * The header text case of the modal
   */
  @state()
  private isHeaderTextCapitalize: boolean | undefined;

  /**
   * The body text of the modal
   */
  @state()
  protected body: ModalBody;

  /**
   * The configuration of the primary button.
   */
  @state()
  protected primaryButton?: ModalButton;

  @state()
  protected primaryButtonFirst?: boolean;

  /**
   * The configuration of the secondary button.
   */
  @state()
  protected secondaryButton?: ModalButton;

  @state()
  protected customButton?: TemplateResult;

  @state()
  protected reversedFooter?: boolean;

  @state()
  protected disableHeader?: boolean;

  @state()
  protected disableFooter?: boolean;

  @state()
  private isModalCentered?: boolean;

  @state()
  protected enableBackgroundBlur?: boolean;

  /**
   * A flag to indicate if the closing cross at the top right of the modal should be shown.
   */
  @state()
  protected showClosingCross?: boolean;

  /**
   * The Modal Context/Configuration
   */
  @state()
  private modalContext: ModalContext;

  @state()
  protected modalSize?: ModalSize;

  /**
   * The Modal A11y
   */
  @state()
  private a11y: ModalA11y;

  @state()
  private modalInstance: any;

  @state()
  private triggerButton: any;

  @state()
  private emitCloseEvent = true;

  @state()
  protected modalContentId?: string;

  private selector = '.modal';

  constructor({
    body,
    header,
    headerClassName = '',
    isHeaderTextBold = false,
    isHeaderTextCapitalize = false,
    primaryButton = undefined,
    primaryButtonFirst = false,
    secondaryButton = undefined,
    modalContext,
    customButton = html``,
    reversedFooter = false,
    showClosingCross = true,
    disableHeader = false,
    disableFooter = false,
    a11y = {
      announceBody: false,
    },
    isModalCentered = false,
    modalContentId = undefined,
    enableBackgroundBlur = false,
    modalSize = ModalSize.DEFAULT,
  }: ModalConfig) {
    super();
    isHeaderTextBold ? (this.isHeaderTextBold = isHeaderTextBold) : false;
    isHeaderTextCapitalize ? (this.isHeaderTextCapitalize = isHeaderTextCapitalize) : false;
    reversedFooter ? (this.reversedFooter = reversedFooter) : false;
    primaryButton ? (this.primaryButton = primaryButton) : undefined;
    this.primaryButtonFirst = primaryButtonFirst;
    customButton ? (this.customButton = customButton) : '';
    secondaryButton ? (this.secondaryButton = secondaryButton) : undefined;
    isModalCentered ? (this.isModalCentered = isModalCentered) : false;
    enableBackgroundBlur ? (this.enableBackgroundBlur = enableBackgroundBlur) : false;
    this.modalContentId = modalContentId;

    this.body = body;
    this.header = header;
    this.headerClassName = headerClassName;
    this.modalContext = modalContext;
    this.showClosingCross = showClosingCross;
    this.disableHeader = disableHeader;
    this.disableFooter = disableFooter;
    this.a11y = a11y;
    this.modalSize = modalSize;
  }

  renderComp() {
    return html`
      <div
        class="modal fade ${this.enableBackgroundBlur ? 'bg-blur' : ''}"
        tabindex="-1"
        aria-hidden="false"
        aria-modal="true"
        aria-describedby="${ifDefined(
          this.a11y.announceBody ? this.modalContext.componentRef + '-modal-body' : undefined
        )}"
        aria-labelledby="${this.modalContext.componentRef}-modal-header"
      >
        <div class="modal-dialog ${this.isModalCentered ? 'modal-dialog-centered' : ''} modal-fullscreen-md-down ${this.modalSize}" role="document">
          <div class="modal-content" id="${ifDefined(this.modalContentId)}">
            ${!this.disableHeader
              ? html`
                <div class="modal-header">
                  <h2
                    id="${this.modalContext.componentRef}-modal-header"
                    class="fs-5 lh-sm modal-title ${this.isHeaderTextBold ? 'fw-bold' : ''} ${this.isHeaderTextCapitalize ? 'text-capitalize' : ''} ${this.headerClassName}"
                  >
                    ${this.header}
                  </h2>
                  ${this.showClosingCross
                    ? html` <button
                        id="modalCloseButton"
                        type="button"
                        @click=${this.close}
                        data-bs-dismiss="modal"
                        class="btn-close"
                        aria-label="Close"
                      ></button>`
                    : nothing}
                </div>`
              : nothing}
            <div id="${this.modalContext.componentRef}-modal-body" class="modal-body">
              ${this.body?.template}
            </div>
            ${!this.disableFooter
              ? html`<div class="modal-footer ${this.reversedFooter ? 'flex-row-reverse' : ''}">
                  ${this.customButton ? html`${this.customButton}` : nothing}
                  ${this.primaryButtonFirst
                    ? html`${this.renderPrimaryButton()}${this.renderSecondaryButton()}`
                    : html`${this.renderSecondaryButton()}${this.renderPrimaryButton()}`}
                </div>`
              : nothing}
          </div>
        </div>
      </div>
    `;
  }

  private renderPrimaryButton() {
    return this.primaryButton
      ? html` <button
          type="button"
          id="modal-primary-button"
          data-automation-id="${this.modalContext.componentRef}-modal-primary-button"
          @click=${this.confirm}
          class="${this.primaryButton.className ?? 'btn btn-primary'} ${this.primaryButton.showSpinner ? 'btn-stacked' : ''}"
          aria-label="${ifDefined(this.primaryButton.ariaLabel || (!this.primaryButton.label ? 'confirm' : undefined))}"
          ?disabled=${this.primaryButton.isDisabled || this.primaryButton.isLoading}>
          ${this.renderButtonContent(this.primaryButton)}
        </button>`
      : nothing;
  }

  private renderSecondaryButton() {
    return this.secondaryButton
      ? html` <button
          type="button"
          id="modal-secondary-button"
          data-automation-id="${this.modalContext.componentRef}-modal-secondary-button"
          @click=${this.secondaryAction}
          class="${this.secondaryButton.className ?? 'btn btn-outline-primary'} ${this.secondaryButton.showSpinner ? 'btn-stacked' : ''}"
          aria-label="${this.secondaryButton.ariaLabel ?? 'cancel'}"
          data-bs-dismiss="modal"
          ?disabled=${this.secondaryButton.isDisabled || this.secondaryButton.isLoading}>
          ${this.renderButtonContent(this.secondaryButton)}
        </button>`
      : nothing;
  }

  /**
   * If enabled, renders a spinner inside the modal button.
   * The button will retain the size of the original text when showing the spinner.
   */
  private renderButtonContent(button: ModalButton): TemplateResult {
    if (!button.showSpinner) {
      return html`${button.label}`;
    }

    return html`
      <span class="btn-label ${button.isLoading ? 'hidden' : ''}"> ${button.label} </span>
      <span class="spinner-border spinner-border-sm btn-loader ${button.isLoading ? 'active' : ''}" aria-hidden="true"></span>
      <div class="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
        ${this.primaryButton?.isLoading ? this.primaryButton.spinnerMessage ?? 'Loading' : ''}
      </div>
    `;
  }

  public setHeader(header: string) {
    this.header = header;
  }

  public setBody(body: ModalBody) {
    this.body = body;
  }

  public setPrimaryButton(primaryButton: ModalButton) {
    this.primaryButton = primaryButton;
  }

  public setSecondaryButton(secondaryButton: ModalButton) {
    this.secondaryButton = secondaryButton;
  }

  public setModalContext(modalContext: ModalContext) {
    this.modalContext = modalContext;
  }

  public async open(e?: Event) {
    const targetEl = e?.currentTarget as HTMLElement;

    if (targetEl) {
      this.triggerButton = targetEl;
    }

    // appending somewhere before the actual content inside the body, as it was causing focus trap not to work.
    document.body.prepend(this);

    await waitForElementsToLoad(this.shadowRoot!, [this.selector]);

    this.modalInstance = this.bsInit(this.selector, window.bootstrap?.Modal)?.instance;
    this.modalInstance.show();

    this.shadowRoot?.querySelector(this.selector)?.addEventListener('shown.bs.modal', this.setInitalFocus.bind(this), {
      once: true,
    });
    this.shadowRoot
      ?.querySelector(this.selector)
      ?.addEventListener('hidden.bs.modal', this.logCloseEventAndCleanup.bind(this), {
        once: true,
      });
  }

  setInitalFocus() {
    if (!this.showClosingCross) {
      waitForElementAndFocus(this.shadowRoot!, '.modal-body');
    } else {
      waitForElementAndFocus(this.shadowRoot!, '.btn-close');
    }
  }

  logCloseEventAndCleanup() {
    if (this.emitCloseEvent) {
      this.triggerButton?.focus();
      emit({
        element: this.modalContext?.component || this,
        name: `${this.modalContext.componentRef}-modal-close`,
        detail: this.modalContext?.componentRefData,
        dataLayer: true,
        bubbles: true,
      });
    }

    [...document.getElementsByTagName('mhecomm-modal')].forEach((element) => {
      element.remove();
    });
  }

  secondaryAction() {
    const event = new CustomEvent(`${this.modalContext.componentRef}-modal-secondary`, { bubbles: true });
    try {
      this.dispatchEvent(event);
    } catch (exception) {
      console.error(exception);
    } finally {
      this.close();
    }
  }

  close(_e?: Event, emitCloseEvent = true) {
    this.emitCloseEvent = emitCloseEvent;
    this.modalInstance.hide();
  }

  confirm() {
    const event = new CustomEvent('mhecomm-modal-confirmed', { bubbles: true });
    try {
      this.dispatchEvent(event);
    } catch (exception) {
      this.close();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-modal': Modal;
  }
}
